/**
 * BadgeTile — shared V2 component for displaying a loyalty/reward badge.
 *
 * Used on UserProfilePage's BasicInfoTab and MyProfilePage. Renders the
 * badge image (with placeholder fallback), an optional label, and a
 * JS-driven hover popover. The popover is **portaled to document.body**
 * with position: fixed so it escapes every parent stacking context,
 * overflow:hidden, and transform on the way back up to the document root.
 *
 * Coordinates are recomputed on each mouseenter from the badge's
 * getBoundingClientRect; close-on-scroll keeps it glued to the tile.
 *
 * Expected `badge` shape (all fields optional except badge_image for
 * visual fidelity):
 *   {
 *     badge_id?:          number,
 *     badge_image:        string,  // S3 URL
 *     badge_name?:        string,
 *     badge_description?: string,
 *     create_time?:       string,  // MySQL UTC timestamp
 *   }
 */

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { convertIsoUTCToLocalDate } from '../../libs.jsx';
import './badge-tile.css';

const POPOVER_W = 280;

const BadgeTile = ({ badge, tzOffset }) => {
    const ref = useRef(null);
    const [open, setOpen] = useState(false);
    const [style, setStyle] = useState({});

    const name = badge.badge_name || '';
    const desc = badge.badge_description || '';
    // create_time is a MySQL UTC timestamp; convert to campus-local date.
    const earnedDate = badge.create_time
        ? convertIsoUTCToLocalDate(badge.create_time, tzOffset)
        : '';
    const hasPopoverContent = !!(desc || earnedDate || name);

    const handleEnter = () => {
        if (!hasPopoverContent || !ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const popoverW = Math.min(POPOVER_W, window.innerWidth - 16);
        // Center horizontally over the badge tile, then clamp inside the viewport.
        const centerX = rect.left + rect.width / 2;
        let left = Math.round(centerX - popoverW / 2);
        left = Math.max(8, Math.min(left, window.innerWidth - popoverW - 8));
        // Prefer placing the popover above the badge; fall back to below if
        // the badge is too close to the top of the viewport.
        const placeAbove = rect.top >= 140;
        const next = {
            position: 'fixed',
            left: `${left}px`,
            width: `${popoverW}px`,
            zIndex: 9999,
        };
        if (placeAbove) {
            next.bottom = `${Math.round(window.innerHeight - rect.top + 8)}px`;
        } else {
            next.top = `${Math.round(rect.bottom + 8)}px`;
        }
        setStyle(next);
        setOpen(true);
    };
    const handleLeave = () => setOpen(false);

    // Close on scroll — fixed position is relative to the viewport, so a
    // scroll would otherwise leave the popover floating away from its badge.
    useEffect(() => {
        if (!open) return;
        const onScroll = () => setOpen(false);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [open]);

    return (
        <figure
            ref={ref}
            className="v2-badge"
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
            onFocus={handleEnter}
            onBlur={handleLeave}
            tabIndex={hasPopoverContent ? 0 : -1}
        >
            {badge.badge_image ? (
                <img
                    className="v2-badge__img"
                    src={badge.badge_image}
                    alt={name || 'Earned badge'}
                    loading="lazy"
                />
            ) : (
                <div className="v2-badge__placeholder" aria-hidden="true">🏅</div>
            )}
            {name ? <figcaption className="v2-badge__label">{name}</figcaption> : null}

            {open && hasPopoverContent
                ? createPortal(
                    <span className="v2-badge__popover" role="tooltip" style={style}>
                        {name ? <span className="v2-badge__popover-title">{name}</span> : null}
                        {desc ? <span className="v2-badge__popover-desc">{desc}</span> : null}
                        {earnedDate ? <span className="v2-badge__popover-meta">Earned {earnedDate}</span> : null}
                    </span>,
                    document.body
                )
                : null}
        </figure>
    );
};

export default BadgeTile;
